import { createContext, FC, ReactNode, useContext, useState } from 'react';
import { createPortal } from 'react-dom';
import { Outlet, useMatch } from 'react-router-dom';
import { Box } from '@hopskipdrive/react-components';
import { useFlags } from '@rideiq/contexts/LDContext';
import { ActionBar } from '@rideiq/shared/components';
import NotFound from '@rideiq/shared/pages/NotFound';

import { actions, subjects } from '../../contexts/AbilityContext';
import ProtectedRoute from '../../routes/ProtectedRoute';

import { ReportsTabs } from './ReportsTabs';

const ReportsHeaderHostContext = createContext<HTMLDivElement | null | undefined>(undefined);

export const ReportsLayout: FC = () => {
  const { rideiqRiderSummaryReportEnabled } = useFlags();
  const [headerHost, setHeaderHost] = useState<HTMLDivElement | null>(null);
  const isRiderSummaryRoute = Boolean(useMatch('/reports/riders'));
  const isReportHistoryRoute = Boolean(useMatch('/reports/history'));
  const isClientReportRoute = isRiderSummaryRoute || isReportHistoryRoute;
  const allowedAction = isClientReportRoute ? actions.all : actions.read;
  const allowedSubject = isClientReportRoute ? subjects.clientReport : subjects.dashboard;

  return (
    <ProtectedRoute allowedAction={allowedAction} allowedSubject={allowedSubject}>
      {isClientReportRoute && !rideiqRiderSummaryReportEnabled ? (
        <NotFound />
      ) : (
        <ReportsHeaderHostContext.Provider value={headerHost}>
          <div ref={setHeaderHost} data-testid="reports-header-host" style={{ display: 'contents' }} />
          <Box
            display={isClientReportRoute ? 'flex' : 'contents'}
            flexDirection="column"
            gap={isClientReportRoute ? '24px' : 0}
            pb={isClientReportRoute ? '24px' : 0}
          >
            <ReportsTabs />
            <Outlet />
          </Box>
        </ReportsHeaderHostContext.Provider>
      )}
    </ProtectedRoute>
  );
};

export const ReportsHeader: FC<{ children: ReactNode }> = ({ children }) => {
  const headerHost = useContext(ReportsHeaderHostContext);

  if (headerHost === undefined) {
    throw new Error('ReportsHeader must be rendered within ReportsLayout');
  }

  if (!headerHost) {
    return null;
  }

  return createPortal(<ActionBar>{children}</ActionBar>, headerHost);
};
